import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDatePicker,
} from '@mui/lab';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  Link,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';
import { DateRange as DateRangeIcon } from '@mui/icons-material';

import {
  getCurriculumsV2,
  getCurriculumV2State,
} from '../../../../store/V2/CurriculumSlice';
import {
  getUsersState,
  IUserFlexibleRate,
  IUserSubscriptionRate,
  TUpdateUserPlanDataAsAdmin,
  updateUserPlanDataAsAdmin,
} from '../../../../store/UserSlice';
import {
  getOneToOneTutorRateStudentTypesState,
  IOneToOneTutorRateStudentType,
} from '../../../../store/V2/OneToOneTutorRateStudentTypesSlice';
import { IFlexiblePlan } from '../../../../store/V2/FlexiblePlansSlice';
import { ISubscriptionPlan } from '../../../../store/V2/SubscriptionPlansSlice';
import { currencyOptions } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import { checkUserPermission } from '../../../../helper';
import OneToOneTutorRateStudentTypeListDropdown, {
  TOneToOneTutorRateStudentTypeOption,
} from '../../../../components/Inputs/admin/OneToOneTutorRateStudentTypeListDropdown';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../components/Inputs';
import { primaryButtonProps } from '../AdminViewUser';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/V2/RolesSlice';
import {
  getStudentFlexibleRatesState,
  getUserFlexibleRatesAsAdmin,
  resetAdminUserFlexibleRates,
  TGetUserFlexibleRates,
} from '../../../../store/V2/StudentFlexibleRatesSlice';
import {
  getStudentSubscriptionRatesState,
  getUserSubscriptionRatesAsAdmin,
  resetAdminUserSubscriptionRates,
  TGetUserSubscriptionRates,
} from '../../../../store/V2/StudentSubscriptionRatesSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { getTenantsState } from '../../../../store/V2/TenantsSlice';
import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../components/V2/Buttons';

type TSelectOption = {
  label: string;
  value: string;
};

type TFlexibleRateItem = {
  id: string;
  planId: string;
  planName: string;
  defaultValue: string;
  value: string;
  currency: string;
  country?: string;
  curriculum?: string;
  grade?: string;
};

type TSubscriptionRateItem = {
  id: string;
  planId: string;
  planName: string;
  defaultValue: string;
  value: string;
  currency: string;
  country?: string;
  curriculum?: string;
  grade?: string;
};

type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedOneToOneStudentType: TOneToOneTutorRateStudentTypeOption | null;
  premiumExpirationDate?: Moment | null;
};

export const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  selectedCurriculum: null,
  selectedGrade: null,
  selectedOneToOneStudentType: null,
  premiumExpirationDate: null,
};

const Plans: React.FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const usersState = useAppSelector(getUsersState);
  const curriculumState = useAppSelector(getCurriculumV2State);
  const studentFlexibleRatesState = useAppSelector(
    getStudentFlexibleRatesState
  );

  const oneToOneTutorRateStudentTypesState = useAppSelector(
    getOneToOneTutorRateStudentTypesState
  );

  const studentSubscriptionRatesState = useAppSelector(
    getStudentSubscriptionRatesState
  );

  const { user: authUser } = authState;
  const { appTenant } = tenantState;
  const { user: userData } = usersState;

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setLoading] = useState(false);
  const [isLinkCopied, setLinkCopied] = useState<boolean>(false);
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [flexibleRateList, setFlexibleRateList] = useState<TFlexibleRateItem[]>(
    []
  );

  const [subscriptionRateList, setSubscriptionRateList] = useState<
    TSubscriptionRateItem[]
  >([]);

  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  const isSubmitDisabled = false;

  let defaultStudentType: IOneToOneTutorRateStudentType | undefined;

  if (
    oneToOneTutorRateStudentTypesState.adminOneToOneTutorRateStudentTypeListData &&
    userData
  ) {
    const _studentTypeList =
      oneToOneTutorRateStudentTypesState
        .adminOneToOneTutorRateStudentTypeListData.list || [];

    const _match = _studentTypeList.find((st) =>
      st.grades.some(
        (stg) =>
          userData.curriculum === stg.curriculum && userData.grade === stg.grade
      )
    );

    if (_match) {
      defaultStudentType = _match;
    }
  }

  const billingLink = `${process.env.REACT_APP_BASE_URL}${process.env.PUBLIC_URL}/portal/billing/${userData?._id}`;

  useEffect(() => {
    dispatch(getCurriculumsV2());

    if (userData) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (userData.curriculum) {
          newStateData.selectedCurriculum = {
            label: userData.curriculum,
            value: userData.curriculum,
          };
        }

        if (userData.grade) {
          newStateData.selectedGrade = {
            label: userData.grade,
            value: userData.grade,
          };
        }

        if (userData.oneToOneStudentType) {
          const _oneToOneStudentType = userData.oneToOneStudentType as IOneToOneTutorRateStudentType;

          newStateData.selectedOneToOneStudentType = {
            label: _oneToOneStudentType.name,
            value: _oneToOneStudentType._id,
          };
        }

        if (userData.premiumAccess && userData.premiumAccess.expiresAt) {
          newStateData.premiumExpirationDate = moment(
            userData.premiumAccess.expiresAt
          );
        }

        return newStateData;
      });

      const flexibleApiData: TGetUserFlexibleRates = {
        userId: userData._id,
      };

      if (userData.curriculum) {
        flexibleApiData.curriculum = userData.curriculum;
      }

      if (userData.grade) {
        flexibleApiData.grade = userData.grade;
      }

      dispatch(getUserFlexibleRatesAsAdmin(flexibleApiData));

      const subscriptionApiData: TGetUserSubscriptionRates = {
        userId: userData._id,
      };

      if (userData.curriculum) {
        subscriptionApiData.curriculum = userData.curriculum;
      }

      if (userData.grade) {
        subscriptionApiData.grade = userData.grade;
      }

      dispatch(getUserSubscriptionRatesAsAdmin(subscriptionApiData));
    }

    return () => {
      dispatch(resetAdminUserFlexibleRates());
      dispatch(resetAdminUserSubscriptionRates());
    };
  }, []);

  useEffect(() => {
    setFlexibleRateData(userData?.flexibleRates);
  }, [studentFlexibleRatesState.adminUserFlexibleRates]);

  useEffect(() => {
    setSubscriptionRateData(userData?.subscriptionRates);
  }, [studentSubscriptionRatesState.adminUserSubscriptionRates]);

  useEffect(() => {
    if (curriculumState.list && curriculumState.list.length) {
      setCurriculumOptions(
        curriculumState.list.map((curriculumItem) => ({
          label: curriculumItem.curriculum,
          value: curriculumItem.curriculum,
        }))
      );

      const matchingCurriculum = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        matchingCurriculum &&
        matchingCurriculum.subjects &&
        matchingCurriculum.subjects.length
      ) {
        const newGradeOptions: TSelectOption[] = [];

        matchingCurriculum.subjects.forEach((subjectItem) => {
          const grades = subjectItem.grade || [];

          grades.forEach((gradeItem) => {
            if (
              !newGradeOptions.some((item) => item.value === gradeItem.grade)
            ) {
              newGradeOptions.push({
                label: gradeItem.grade,
                value: gradeItem.grade,
              });
            }
          });
        });

        setGradeOptions(newGradeOptions);
      }
    }
  }, [curriculumState.list]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const setFlexibleRateData = (flexibleRates?: IUserFlexibleRate[]) => {
    if (studentFlexibleRatesState.adminUserFlexibleRates.length) {
      setFlexibleRateList(
        studentFlexibleRatesState.adminUserFlexibleRates.map(
          (flexibleRateItem) => {
            const amounts = flexibleRateItem.amounts || [];
            const packageData = flexibleRateItem.package as IFlexiblePlan;
            const defaultAmountData = amounts[0];

            let currency = 'SGD';
            let value = '';

            if (defaultAmountData) {
              currency = defaultAmountData.currency || 'SGD';
            }

            if (flexibleRates) {
              const matchingFlexibleRate = flexibleRates.find((item) => {
                const _plan = item.planId as IFlexiblePlan;

                return (
                  packageData._id === _plan._id &&
                  flexibleRateItem.country === item.country &&
                  flexibleRateItem.curriculum === item.curriculum &&
                  flexibleRateItem.grade === item.grade
                );
              });

              if (matchingFlexibleRate) {
                if (matchingFlexibleRate) {
                  currency = matchingFlexibleRate.currency;
                  value = matchingFlexibleRate.amount.toString();
                }
              }
            }

            return {
              value,
              currency,
              id: flexibleRateItem._id,
              planId: packageData._id,
              planName: packageData.name,
              defaultValue:
                defaultAmountData && defaultAmountData.amount
                  ? defaultAmountData.amount.toString()
                  : '',
              country: flexibleRateItem.country,
              curriculum: flexibleRateItem.curriculum,
              grade: flexibleRateItem.grade,
            };
          }
        )
      );
    }
  };

  const setSubscriptionRateData = (
    subscriptionRates?: IUserSubscriptionRate[]
  ) => {
    if (studentSubscriptionRatesState.adminUserSubscriptionRates.length) {
      setSubscriptionRateList(
        studentSubscriptionRatesState.adminUserSubscriptionRates.map(
          (subscriptionRateItem) => {
            const amounts = subscriptionRateItem.amounts || [];
            const packageData = subscriptionRateItem.package as ISubscriptionPlan;
            const defaultAmountData = amounts[0];

            let currency = 'SGD';
            let value = '';

            if (defaultAmountData) {
              currency = defaultAmountData.currency || 'SGD';
            }

            if (subscriptionRates) {
              const matchingSubscriptionRate = subscriptionRates.find(
                (item) => {
                  const _plan = item.planId as ISubscriptionPlan;

                  return (
                    packageData._id === _plan._id &&
                    subscriptionRateItem.country === item.country &&
                    subscriptionRateItem.curriculum === item.curriculum &&
                    subscriptionRateItem.grade === item.grade
                  );
                }
              );

              if (matchingSubscriptionRate) {
                currency = matchingSubscriptionRate.currency;
                value = matchingSubscriptionRate.amount.toString();
              }
            }

            return {
              value,
              currency,
              id: subscriptionRateItem._id,
              planId: packageData._id,
              planName: packageData.name,
              defaultValue:
                defaultAmountData && defaultAmountData.amount
                  ? defaultAmountData.amount.toString()
                  : '',
              country: subscriptionRateItem.country,
              curriculum: subscriptionRateItem.curriculum,
              grade: subscriptionRateItem.grade,
            };
          }
        )
      );
    }
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (curriculumState.list && curriculumState.list.length && value) {
      const matchingCurriculum = curriculumState.list.find(
        (item) => item.curriculum === value.value
      );

      if (
        matchingCurriculum &&
        matchingCurriculum.subjects &&
        matchingCurriculum.subjects.length
      ) {
        matchingCurriculum.subjects.forEach((subjectItem) => {
          const grades = subjectItem.grade || [];

          grades.forEach((gradeItem) => {
            if (
              !newGradeOptions.some((item) => item.value === gradeItem.grade)
            ) {
              newGradeOptions.push({
                label: gradeItem.grade,
                value: gradeItem.grade,
              });
            }
          });
        });
      }
    }

    updateStateAttributes({
      selectedCurriculum: value,
      selectedGrade: null,
    });

    setGradeOptions(newGradeOptions);

    dispatch(resetAdminUserFlexibleRates());
    dispatch(resetAdminUserSubscriptionRates());
    setFlexibleRateList([]);
    setSubscriptionRateList([]);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    updateStateAttributes({
      selectedGrade: value,
    });

    dispatch(resetAdminUserFlexibleRates());
    dispatch(resetAdminUserSubscriptionRates());
    setFlexibleRateList([]);
    setSubscriptionRateList([]);

    if (userData && state.selectedCurriculum) {
      dispatch(
        getUserFlexibleRatesAsAdmin({
          userId: userData._id,
          curriculum: state.selectedCurriculum.value,
          grade: value ? value.value : undefined,
        })
      );

      dispatch(
        getUserSubscriptionRatesAsAdmin({
          userId: userData._id,
          curriculum: state.selectedCurriculum.value,
          grade: value ? value.value : undefined,
        })
      );
    }
  };

  const handleFlexibleRateItemAmountChange = (
    flexibleRateItem: TFlexibleRateItem,
    value: string
  ) => {
    setFlexibleRateList((prevState) => {
      const newRateList = [...prevState];

      const index = newRateList.findIndex(
        (item) => item.id === flexibleRateItem.id
      );

      if (index > -1 && newRateList[index]) {
        newRateList[index] = {
          ...newRateList[index],
          value,
        };
      }

      return newRateList;
    });
  };

  const handleFlexibleRateItemCurrencyChange = (
    flexibleRateItem: TFlexibleRateItem,
    value: TSelectOption
  ) => {
    setFlexibleRateList((prevState) => {
      const newRateList = [...prevState];

      const index = newRateList.findIndex(
        (item) => item.id === flexibleRateItem.id
      );

      if (index > -1 && newRateList[index]) {
        newRateList[index] = {
          ...newRateList[index],
          currency: value.value,
        };
      }

      return newRateList;
    });
  };

  const handleSubscriptionRateItemAmountChange = (
    subscriptionRateItem: TSubscriptionRateItem,
    value: string
  ) => {
    setSubscriptionRateList((prevState) => {
      const newRateList = [...prevState];

      const index = newRateList.findIndex(
        (item) => item.id === subscriptionRateItem.id
      );

      if (index > -1 && newRateList[index]) {
        newRateList[index] = {
          ...newRateList[index],
          value,
        };
      }

      return newRateList;
    });
  };

  const handleSubscriptionRateItemCurrencyChange = (
    subscriptionRateItem: TSubscriptionRateItem,
    value: TSelectOption
  ) => {
    setSubscriptionRateList((prevState) => {
      const newRateList = [...prevState];

      const index = newRateList.findIndex(
        (item) => item.id === subscriptionRateItem.id
      );

      if (index > -1 && newRateList[index]) {
        newRateList[index] = {
          ...newRateList[index],
          currency: value.value,
        };
      }

      return newRateList;
    });
  };

  const handleCopyLink = () => {
    window.navigator.clipboard.writeText(billingLink);
    setLinkCopied(true);
    setTimeout(() => {
      setLinkCopied(false);
    }, 5000);
  };

  const handleSubmit = async () => {
    if (userData) {
      setLoading(true);

      const apiData: TUpdateUserPlanDataAsAdmin = {
        id: userData._id,
        flexibleRates: flexibleRateList
          .filter(
            (item) =>
              item.value &&
              item.value.trim().length &&
              parseFloat(item.value) > 0
          )
          .map((item) => ({
            planId: item.planId,
            amount: parseFloat(item.value),
            currency: item.currency,
            country: item.country,
            curriculum: item.curriculum,
            grade: item.grade,
          })),
        subscriptionRates: subscriptionRateList
          .filter(
            (item) =>
              item.value &&
              item.value.trim().length &&
              parseFloat(item.value) > 0
          )
          .map((item) => ({
            planId: item.planId,
            amount: parseFloat(item.value),
            currency: item.currency,
            country: item.country,
            curriculum: item.curriculum,
            grade: item.grade,
          })),
      };

      if (state.selectedCurriculum) {
        apiData.curriculum = state.selectedCurriculum.value;
      }

      if (state.selectedGrade) {
        apiData.grade = state.selectedGrade.value;
      }

      if (state.selectedOneToOneStudentType) {
        apiData.oneToOneStudentType = state.selectedOneToOneStudentType.value;
      }

      if (state.premiumExpirationDate) {
        apiData.premiumExpirationDate = state.premiumExpirationDate
          .clone()
          .toISOString();
      }

      await dispatch(updateUserPlanDataAsAdmin(apiData));

      setLoading(false);
    }
  };

  return (
    <Box>
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        marginBottom="24px"
      >
        <Typography variant="h6" fontSize="18px">
          Plans
        </Typography>
        <LoadingButton
          {...primaryButtonProps}
          loading={isLoading}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          Save
        </LoadingButton>
      </Stack>
      <Grid container spacing={3}>
        {checkUserPermission({
          authUser,
          tenant: appTenant,
          permissions: {
            profile: [USER_PERMISSION_INDIVIDUALS.PROFILE_CURRICULUM],
          },
        }) ? (
          <>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedCurriculum}
                  options={curriculumOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  getOptionKey={(option: TSelectOption) => option.value}
                  onChange={handleCurriculumChange}
                  disabled={
                    studentFlexibleRatesState.status === 'loading' ||
                    studentSubscriptionRatesState.status === 'loading'
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Curriculum"
                      placeholder="Curriculum"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedGrade}
                  options={gradeOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={curriculumState.status === 'loading'}
                  getOptionKey={(option: TSelectOption) => option.value}
                  onChange={handleGradeChange}
                  disabled={
                    !state.selectedCurriculum ||
                    studentFlexibleRatesState.status === 'loading' ||
                    studentSubscriptionRatesState.status === 'loading'
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Grade"
                      placeholder="Grade"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <OneToOneTutorRateStudentTypeListDropdown
                  selectedItem={state.selectedOneToOneStudentType}
                  handleChange={(e, value) =>
                    updateStateAttributes({
                      selectedOneToOneStudentType: value,
                    })
                  }
                  componentProps={{
                    fullWidth: true,
                  }}
                  textFieldProps={{
                    label: 'One to One Student Type *',
                    placeholder: defaultStudentType
                      ? defaultStudentType.name
                      : 'Select',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
            </Grid>
          </>
        ) : null}
        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <MobileDatePicker
                clearable
                disableCloseOnSelect={false}
                value={state.premiumExpirationDate}
                onChange={(value) =>
                  updateStateAttributes({
                    premiumExpirationDate: value,
                  })
                }
                label="Premium Expiration Date"
                inputFormat="YYYY MMM DD"
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    placeholder="Premium Expiration Date"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <DateRangeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>
      </Grid>
      <Stack
        flexDirection="row"
        alignItems="center"
        marginBottom="24px"
        marginTop="24px"
        gap="10px"
      >
        <Typography variant="h6" fontSize="18px">
          Flexible Plans
        </Typography>
        {studentFlexibleRatesState.status === 'loading' &&
        !flexibleRateList.length ? (
          <CircularProgress size={18} />
        ) : null}
      </Stack>
      <Grid container spacing={3}>
        {flexibleRateList.map((flexibleRateItem, index) => {
          const inputValue = flexibleRateItem.value;
          const currencyOption = currencyOptions.find(
            (item) => item.value === flexibleRateItem.currency
          ) || { label: 'SGD', value: 'SGD' };

          return (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              key={`flexible-plan-rate-item-${index}`}
            >
              <FormControl fullWidth>
                <Typography fontSize="12px" fontWeight={600} mb="10px">
                  {flexibleRateItem.planName}
                </Typography>
                <Stack direction="row" alignItems="center" gap="5px">
                  <TPTextField
                    {...textFieldProps}
                    fullWidth
                    type="number"
                    size="small"
                    label="Amount"
                    placeholder={flexibleRateItem.defaultValue}
                    value={inputValue}
                    onChange={(event) =>
                      handleFlexibleRateItemAmountChange(
                        flexibleRateItem,
                        event.target.value
                      )
                    }
                  />
                  <div style={{ minWidth: '100px' }}>
                    <Autocomplete
                      size="small"
                      disableClearable
                      value={currencyOption}
                      options={currencyOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      isOptionEqualToValue={(option, value) =>
                        option.label === value.value
                      }
                      onChange={(event, value) =>
                        handleFlexibleRateItemCurrencyChange(
                          flexibleRateItem,
                          value
                        )
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          label="Currency *"
                          placeholder="Currency"
                        />
                      )}
                    />
                  </div>
                </Stack>
              </FormControl>
            </Grid>
          );
        })}
        {studentFlexibleRatesState.status !== 'loading' &&
        !flexibleRateList.length ? (
          <Grid item xs={12}>
            <Typography>No flexible rates are available</Typography>
          </Grid>
        ) : null}
      </Grid>
      <Stack
        flexDirection="row"
        alignItems="center"
        marginBottom="24px"
        marginTop="24px"
        gap="10px"
      >
        <Typography variant="h6" fontSize="18px">
          Subscription Plans
        </Typography>
        {studentSubscriptionRatesState.status === 'loading' &&
        !subscriptionRateList.length ? (
          <CircularProgress size={18} />
        ) : null}
      </Stack>
      <Grid container spacing={3}>
        {subscriptionRateList.map((subscriptionRateItem, index) => {
          const inputValue = subscriptionRateItem.value;
          const currencyOption = currencyOptions.find(
            (item) => item.value === subscriptionRateItem.currency
          ) || { label: 'SGD', value: 'SGD' };

          return (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              key={`subscription-plan-rate-item-${index}`}
            >
              <FormControl fullWidth>
                <Typography fontSize="12px" fontWeight={600} mb="10px">
                  {subscriptionRateItem.planName}
                </Typography>
                <Stack direction="row" alignItems="center" gap="5px">
                  <TPTextField
                    {...textFieldProps}
                    fullWidth
                    type="number"
                    size="small"
                    label="Amount"
                    placeholder={subscriptionRateItem.defaultValue}
                    value={inputValue}
                    onChange={(event) =>
                      handleSubscriptionRateItemAmountChange(
                        subscriptionRateItem,
                        event.target.value
                      )
                    }
                  />
                  <div style={{ minWidth: '100px' }}>
                    <Autocomplete
                      size="small"
                      disableClearable
                      value={currencyOption}
                      options={currencyOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      isOptionEqualToValue={(option, value) =>
                        option.label === value.value
                      }
                      onChange={(event, value) =>
                        handleSubscriptionRateItemCurrencyChange(
                          subscriptionRateItem,
                          value
                        )
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          label="Currency *"
                          placeholder="Currency"
                        />
                      )}
                    />
                  </div>
                </Stack>
              </FormControl>
            </Grid>
          );
        })}
        {studentSubscriptionRatesState.status !== 'loading' &&
        !subscriptionRateList.length ? (
          <Grid item xs={12}>
            <Typography>No subscription rates are available</Typography>
          </Grid>
        ) : null}
      </Grid>

      <Stack
        flexDirection="row"
        alignItems="center"
        marginBottom="8px"
        marginTop="24px"
        gap="10px"
      >
        <Typography variant="h6" fontSize="18px">
          Payment Link
        </Typography>
      </Stack>
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        gap="10px"
      >
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: { xs: '200px', sm: '250px', md: '400px' },
          }}
        >
          <Link href={billingLink} target="_blank" underline="hover">
            {billingLink}
          </Link>
        </Box>

        <DarkButton
          disableElevation
          color="inherit"
          variant="contained"
          onClick={handleCopyLink}
          disabled={isLoading}
          sx={{ ...defaultButtonStyles }}
        >
          {isLinkCopied ? 'Copied' : 'Copy Link'}
        </DarkButton>
      </Stack>
    </Box>
  );
};

export default Plans;
